import { action, observable } from "mobx";
import _ from "lodash";
import { DomainStore } from "./domainStore";
import { toastError, toastSuccess } from "../domain/errorHandling/toaster";
import { lazyObservable } from "../domain/helpers/lazyLoad";
import enchargeAPI from "./persistence/enchargeAPI";

import { getQuery } from "domain/helpers/query";
import { GetCommunicationPreferencesDataResponse } from "store/persistence/api_client/generated/src";
import { CommunicationCategoriesPreferences } from "encharge-domain/definitions/CommunicationCategory";
import {
  fillTrackingConsentText,
  trackingConsentTexts,
} from "encharge-domain/lib/helpers/trackingConsentTexts";

export class CommunicationPreferencesStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;
  }

  @observable
  preferencesData = lazyObservable<CommunicationPreferencesAPIResponse>(
    (sink, onError) => {
      // clear any errors
      this.rootStore.uiStore.communicationPreferences.setError();
      enchargeAPI
        .getCommunicationPreferencesData(getQuery()?.["t"])
        .then((preferences) => {
          const prefsLocal = _.omit(preferences, ["_response"]);
          prefsLocal.categoriesForPerson = _.mapKeys(
            preferences.categoriesForPerson,
            (_val, key) => Number(key)
          );
          sink(preferences as CommunicationPreferencesAPIResponse);
        })
        .catch((e) => {
          // use this to display the error to the user
          this.rootStore.uiStore.communicationPreferences.setError(
            e?.details?.error?.message || e?.message
          );
          onError(e);
        });
    }
  );

  @action
  async updateCommunicationPreferences(
    personCategories: CommunicationCategoriesPreferences
  ) {
    this.rootStore.uiStore.communicationPreferencesLoading.startLoading();
    const token = getQuery()?.["t"];
    // set null values to "unspecified" to avoid sending "null" to the API
    const personCategoriesWithoutNil = _.mapValues(personCategories, (val) =>
      !val ? "unspecified" : val
    );
    try {
      await enchargeAPI.saveCommunicationPreferences(
        token,
        personCategoriesWithoutNil
      );
      toastSuccess("Preferences updated.");
    } catch (e) {
      toastError({
        message: "Error while updating communication preferences.",
        extra: e,
      });
    } finally {
      this.rootStore.uiStore.communicationPreferencesLoading.finishLoading();
    }
  }

  /**
   * Save the email tracking consent. Changes only trackingConsent, never the
   * subscription or the categories. Resolves to true when it was saved.
   * The text the person saw is not sent: the server composes it, with the same
   * function that drew it on the page, and keeps it with the choice.
   */
  @action
  async updateTrackingConsent(
    trackingConsent: "granted" | "denied",
    source: "preferences page" | "stop-tracking link" = "preferences page"
  ): Promise<boolean> {
    const token = getQuery()?.["t"];
    // the account's own wording for the page, when it has one, with the
    // address and the account's name filled in like on the page itself
    const getTranslation = (key: string) => {
      const data = this.preferencesData.current();
      return fillTrackingConsentText(
        data?.communicationPreferencesSnippets?.[key] || key,
        { email: data?.person?.email, accountName: data?.accountName }
      );
    };
    try {
      await enchargeAPI.saveTrackingConsent(token, {
        trackingConsent,
        source,
      });
      toastSuccess(getTranslation(trackingConsentTexts.saved));
      return true;
    } catch (e) {
      toastError({
        message: getTranslation(trackingConsentTexts.saveError),
        extra: e,
      });
      return false;
    }
  }

  @action
  async unsubscribeFromAllCommunications() {
    this.rootStore.uiStore.communicationPreferencesLoading.startLoading();
    const token = getQuery()?.["t"];
    try {
      await enchargeAPI.unsubscribeFromAllCommunications(token);
      toastSuccess("You have been unsubscribed from all emails.");
    } catch (e) {
      toastError({
        message: "Error while updating communication preferences.",
        extra: e,
      });
    } finally {
      this.rootStore.uiStore.communicationPreferencesLoading.finishLoading();
    }
  }
}

export interface CommunicationPreferencesAPIResponse
  extends Omit<
    GetCommunicationPreferencesDataResponse,
    "_response" | "categoriesForPerson"
  > {
  categoriesForPerson: CommunicationCategoriesPreferences;
}
